import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { selectPositionLtpsSignature } from '../../selectors/security'
import { getPositionsSecurityKeys, buildLtpsByKey } from '../../util/positionLtpsSignature'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { toLocaleStringIn } from '../../util/general'
import type { Position } from '../../proto/generated/position'
import { computePositionsPnLTotals } from '../../util/positionsTotalPnL'

export type PositionsTotalPnLProps = {
    positions: Position[]
    /** Sidebar header: REALISED + TOTAL. Quick widget title: single combined P/L. */
    variant?: 'sidebar' | 'compact'
}

const PositionsTotalPnL: React.FC<PositionsTotalPnLProps> = ({ positions, variant = 'sidebar' }) => {
    const positionKeys = useMemo(() => getPositionsSecurityKeys(positions), [positions])
    const ltpsSignature = useSelector(selectPositionLtpsSignature(positionKeys))
    const [priceMultipliers, setPriceMultipliers] = useState<Map<string, number>>(new Map())

    useEffect(() => {
        if (!positionKeys.length) {
            setPriceMultipliers(new Map())
            return
        }
        let cancelled = false
        Promise.all(positionKeys.map((k) => getSecurityAsync(k))).then((securities) => {
            if (cancelled) return
            const map = new Map<string, number>()
            positionKeys.forEach((k, i) => {
                map.set(k, securities[i]?.priceMultiplier ?? 100)
            })
            setPriceMultipliers(map)
        })
        return () => {
            cancelled = true
        }
    }, [positionKeys.join('\x1e')])

    const totals = useMemo(() => {
        if (!positions.length) {
            return { unrealised: 0, realised: 0 }
        }
        const ltpsByKey = buildLtpsByKey(positionKeys, ltpsSignature)
        return computePositionsPnLTotals(positions, ltpsByKey, priceMultipliers)
    }, [positions, positionKeys, ltpsSignature, priceMultipliers])

    if (!positions.length) {
        return null
    }

    if (variant === 'compact') {
        const total = totals.unrealised + totals.realised
        return (
            <Typography fontSize={14}>
                <span className={total < 0 ? 'red-text' : 'green-text'}>{toLocaleStringIn(total)}</span>
            </Typography>
        )
    }

    const total = totals.realised + totals.unrealised
    const { realised } = totals

    return (
        <Typography
            className="font-feature-num"
            variant="caption"
            sx={{
                color: (theme) => theme.palette.text.secondary,
                mr: 1,
                lineHeight: (theme) => theme.typography.body1.lineHeight,
            }}
        >
            REALISED:{' '}
            <span
                className={
                    realised === 0 ? undefined : realised > 0 ? 'green-text' : 'red-text'
                }
            >
                {toLocaleStringIn(realised)}
            </span>
            {' | TOTAL: '}
            <span
                className={total === 0 ? undefined : total > 0 ? 'green-text' : 'red-text'}
            >
                {toLocaleStringIn(total)}
            </span>
        </Typography>
    )
}

export default memo(PositionsTotalPnL, (prev, next) => prev.positions === next.positions && prev.variant === next.variant)
